import { useSyncExternalStore, useCallback } from 'react';
import { useOrgKey } from '@reflex/ui/client/context';
import type { GitHubRepo } from '@reflex/shared';

const STORAGE_KEY_PREFIX = 'favorite-repos';

/**
 * Favorites are stored per organization. The repo picker shows different
 * repo universes per org (each org has its own GitHub App installations),
 * so a browser-global list would surface one tenant's private repo names
 * inside another tenant's picker. Favorites saved under the historical
 * un-namespaced `favorite-repos` key are intentionally NOT migrated - we
 * can't know which org they belong to, and defaulting them into every org
 * would recreate the leak.
 */
export function favoriteReposStorageKey(orgKey: string): string {
  return `${STORAGE_KEY_PREFIX}:${orgKey}`;
}

interface StoredFavorites {
  repos: GitHubRepo[];
}

const EMPTY: StoredFavorites = { repos: [] };

function readStore(key: string): StoredFavorites {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as StoredFavorites;
    if (!Array.isArray(parsed.repos)) return EMPTY;
    return parsed;
  } catch {
    return EMPTY;
  }
}

// Per-storage-key snapshot cache so `getSnapshot` returns a referentially
// stable object between writes (required by useSyncExternalStore).
const snapshots = new Map<string, StoredFavorites>();
const listeners = new Set<() => void>();

function notify() {
  for (const cb of listeners) cb();
}

function getSnapshot(key: string): StoredFavorites {
  let snap = snapshots.get(key);
  if (!snap) {
    snap = readStore(key);
    snapshots.set(key, snap);
  }
  return snap;
}

function writeStore(key: string, data: StoredFavorites) {
  snapshots.set(key, data);
  localStorage.setItem(key, JSON.stringify(data));
  // Same-tab subscribers share this module store, so notify them directly.
  // Other tabs pick up the change through the browser's native `storage`
  // event (favorites are per-account, so cross-tab sync is intentional).
  notify();
}

// One window listener for cross-tab sync: the browser fires a native `storage`
// event in *other* tabs when this origin's localStorage changes. Attached once
// for the page lifetime rather than per subscribe.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === null) {
      snapshots.clear();
      notify();
    } else if (e.key.startsWith(`${STORAGE_KEY_PREFIX}:`)) {
      snapshots.delete(e.key);
      notify();
    }
  });
}

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

export function useFavoriteRepos() {
  const key = favoriteReposStorageKey(useOrgKey());
  const store = useSyncExternalStore(
    subscribe,
    () => getSnapshot(key),
    () => getSnapshot(key),
  );

  const isFavorite = useCallback(
    (fullName: string) => store.repos.some((r) => r.fullName === fullName),
    [store],
  );

  const toggleFavorite = useCallback(
    (repo: GitHubRepo) => {
      const current = getSnapshot(key);
      const exists = current.repos.some((r) => r.fullName === repo.fullName);
      const next = exists
        ? { repos: current.repos.filter((r) => r.fullName !== repo.fullName) }
        : { repos: [repo, ...current.repos] };
      writeStore(key, next);
    },
    [key],
  );

  return {
    favorites: store.repos,
    isFavorite,
    toggleFavorite,
  };
}
